import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from 'react';
import api, { setAuthToken, clearAuthToken, getAuthToken, setDefaultTenant } from '@/lib/api';
import type { Tenant } from '@/types/models';

export interface AuthUser {
  id: string;
  name: string;
  email: string;
  roles: string[];
  tenant_id: string;
  tenant: Tenant;
  is_super_admin: boolean;
}

interface AuthContextType {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const DEFAULT_TENANT_FEATURES: Tenant['features'] = {
  crm_enabled: false,
  vapi_enabled: false,
};

function normalizeFeatureKey(key: string) {
  const normalized = key.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
  return normalized.endsWith('_enabled') ? normalized : `${normalized}_enabled`;
}

function normalizeTenantFeatures(features: unknown): Tenant['features'] {
  if (Array.isArray(features)) {
    return features.reduce<Tenant['features']>((acc, feature) => {
      if (typeof feature === 'string' && feature.trim()) {
        acc[normalizeFeatureKey(feature)] = true;
      } else if (feature && typeof feature === 'object') {
        const record = feature as Record<string, unknown>;
        const candidate = record.key ?? record.name ?? record.slug ?? record.code;
        if (typeof candidate === 'string' && candidate.trim()) {
          acc[normalizeFeatureKey(candidate)] = true;
        }
      }
      return acc;
    }, { ...DEFAULT_TENANT_FEATURES });
  }

  if (features && typeof features === 'object') {
    return Object.entries(features as Record<string, unknown>).reduce<Tenant['features']>(
      (acc, [key, value]) => {
        acc[normalizeFeatureKey(key)] = Boolean(value);
        return acc;
      },
      { ...DEFAULT_TENANT_FEATURES }
    );
  }

  return { ...DEFAULT_TENANT_FEATURES };
}

function normalizeRoles(roles: unknown): string[] {
  if (!Array.isArray(roles)) return [];

  return roles
    .map((role) => {
      if (typeof role === 'string') return role;
      if (role && typeof role === 'object' && typeof (role as { name?: unknown }).name === 'string') {
        return (role as { name: string }).name;
      }
      return null;
    })
    .filter((role): role is string => Boolean(role));
}

function normalizeTenant(record: Record<string, unknown>): Tenant {
  const rawTenant = (record.tenant ?? record.organization ?? (record.site as { organization?: unknown } | undefined)?.organization ?? {}) as Record<string, unknown>;
  const tenantId = String(rawTenant.id ?? record.tenant_id ?? (record.site as { organization_id?: unknown } | undefined)?.organization_id ?? '');

  return {
    id: tenantId,
    name: String(rawTenant.name ?? 'Organization'),
    slug: String(rawTenant.slug ?? 'organization'),
    logo_url: typeof rawTenant.logo_url === 'string' ? rawTenant.logo_url : undefined,
    features: normalizeTenantFeatures(rawTenant.features),
    is_active: rawTenant.is_active === undefined ? true : Boolean(rawTenant.is_active),
    created_at: String(rawTenant.created_at ?? ''),
    updated_at: String(rawTenant.updated_at ?? ''),
  };
}

function normalizeAuthUser(input: unknown): AuthUser {
  const record = (input ?? {}) as Record<string, unknown>;
  const tenant = normalizeTenant(record);

  return {
    id: String(record.id ?? ''),
    name: String(record.name ?? ''),
    email: String(record.email ?? ''),
    roles: normalizeRoles(record.roles),
    tenant_id: String(record.tenant_id ?? tenant.id),
    tenant,
    is_super_admin: Boolean(record.is_super_admin),
  };
}

const IS_DEMO = !import.meta.env.VITE_API_URL && (window.location.hostname.includes('lovable.app') || window.location.hostname.includes('lovableproject.com'));

const DEMO_USER: AuthUser = {
  id: 'demo-001',
  name: 'Demo Admin',
  email: 'demo@example.com',
  roles: ['admin'],
  tenant_id: 'demo-tenant',
  tenant: {
    id: 'demo-tenant',
    name: 'Demo Organization',
    slug: 'demo-org',
    features: { crm_enabled: true, vapi_enabled: true },
    is_active: true,
    created_at: '',
    updated_at: '',
  },
  is_super_admin: true,
};

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(IS_DEMO ? DEMO_USER : null);
  const [isLoading, setIsLoading] = useState(!IS_DEMO);

  useEffect(() => {
    if (IS_DEMO) return;

    let isMounted = true;

    const bootstrapAuth = async () => {
      const token = getAuthToken();

      if (!token) {
        if (isMounted) {
          setUser(null);
          setIsLoading(false);
        }
        return;
      }

      try {
        const res = await api.get('/api/user');
        const nextUser = normalizeAuthUser(res.data.user ?? res.data);
        if (isMounted) {
          setDefaultTenant(nextUser.tenant_id || null);
          setUser(nextUser);
        }
      } catch {
        clearAuthToken();
        if (isMounted) {
          setUser(null);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    void bootstrapAuth();

    return () => {
      isMounted = false;
    };
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const res = await api.post('/api/login', { email, password });
    setAuthToken(res.data.token);
    const userRes = await api.get('/api/user');
    const nextUser = normalizeAuthUser(userRes.data.user ?? userRes.data);
    setDefaultTenant(nextUser.tenant_id || null);
    setUser(nextUser);
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post('/api/logout');
    } catch {
      // Token may already be invalid
    } finally {
      clearAuthToken();
      setDefaultTenant(null);
      setUser(null);
    }
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading,
        login,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): AuthContextType {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
